import api from './api';

export interface EvohomeDevice {
  id: number;
  external_id: string;
  nombre: string;
  ui_family?: 'zone';
  zone_type?: string | null;
  model_type?: string | null;
  location_id?: number | null;
  location_nombre?: string | null;
  system_id?: number | null;
  system_mode?: string | null;
  habitacion_id: number | null;
  artefacto_id: number | null;
  artefacto?: { nombre?: string; icono?: string; color_icono?: string } | null;
  online: boolean;
  temperature?: number | null;
  setpoint?: number | null;
  setpoint_mode?: string | null;
  until_ts?: string | null;
  sensor_available?: boolean;
  min_setpoint?: number;
  max_setpoint?: number;
  asking_heat?: boolean;
  enabled?: boolean;
  missing_from_cloud?: boolean;
  /** Orden de la tarjeta dentro de la habitación (Settings / vista usuario). */
  orden?: number;
  /** Orden solo en Clima → Horarios; no afecta la habitación. */
  orden_horario?: number;
  pin_required?: boolean;
  can_control?: boolean;
  can_view?: boolean;
  /** Enchufe Shelly que alimenta la caldera de la locación (null = EvoHome no lo toca). */
  heat_supply_device_id?: number | null;
  /** true mientras el ciclo de frío mantiene ese enchufe encendido con el sistema en Apagada. */
  heat_supply_frost_holding?: boolean | null;
}

export interface EvohomeLocation {
  id: number;
  external_id: string;
  nombre: string;
  timezone?: string | null;
  orden?: number;
  heat_supply_device_id?: number | null;
  frost_margin_c?: number | string | null;
  frost_min_on_minutes?: number | string | null;
  frost_holding?: boolean | null;
  ai_management_enabled?: boolean | null;
}

export interface EvohomeHeatSupplyBody {
  device_id: number | null;
  frost_margin_c: number;
  frost_min_on_minutes: number;
}

export interface EvohomeSystem {
  id: number;
  location_id: number;
  external_id: string;
  system_mode?: string | null;
  allowed_system_modes?: Array<string | { systemMode?: string; mode?: string }>;
  online?: boolean;
}

export interface EvohomeLocationOverview {
  location: EvohomeLocation;
  systems: EvohomeSystem[];
  zones: EvohomeDevice[];
}

export interface EvohomePolicyRow {
  evohome_zone_id: number;
  whitelist: boolean;
  blacklist: boolean;
  require_confirmation?: boolean;
  defined?: boolean;
}

export interface EvohomeTempPoint {
  time_bucket: string;
  activity_ratio?: number | null;
  temperatura_promedio: number | null;
  setpoint_promedio?: number | null;
}

export async function fetchEvohomeAccount(): Promise<any> {
  const res = await api.get('/evohome/account');
  return res.data;
}

export async function evohomeConnect(username: string, password: string): Promise<any> {
  const res = await api.post('/evohome/account/connect', { username, password });
  return res.data;
}

export async function evohomeDisconnectAccount(): Promise<void> {
  await api.delete('/evohome/account');
}

export async function evohomePurgeAccount(): Promise<void> {
  await api.delete('/evohome/account/credentials');
}

export async function evohomeSync(): Promise<any> {
  const res = await api.post('/evohome/sync');
  return res.data;
}

export async function fetchEvohomeAdminSummary(): Promise<{ total: number; unassigned_count: number }> {
  const res = await api.get('/evohome/admin/summary');
  return res.data;
}

export async function fetchEvohomeLocations(): Promise<EvohomeLocation[]> {
  const res = await api.get('/evohome/locations');
  return res.data || [];
}

export async function fetchEvohomeLocationOverview(locationId: number): Promise<EvohomeLocationOverview> {
  const res = await api.get(`/evohome/locations/${locationId}/overview`);
  return res.data;
}

export async function patchEvohomeAiManagement(
  locationId: number,
  enabled: boolean,
): Promise<EvohomeLocation> {
  const res = await api.patch(`/evohome/locations/${locationId}/ai-management`, { enabled });
  return res.data;
}

export async function patchEvohomeHeatSupply(
  locationId: number,
  body: EvohomeHeatSupplyBody,
): Promise<EvohomeLocation> {
  const res = await api.patch(`/evohome/locations/${locationId}/heat-supply`, body);
  return res.data;
}

export async function setEvohomeSystemMode(
  systemId: number,
  mode: string,
  duration_minutes?: number,
): Promise<any> {
  const res = await api.post(`/evohome/system/${systemId}/mode`, {
    mode,
    ...(duration_minutes != null ? { duration_minutes } : {}),
  });
  return res.data;
}

export async function fetchEvohomeDevices(): Promise<EvohomeDevice[]> {
  const res = await api.get('/evohome/devices');
  return res.data || [];
}

export async function patchEvohomeDevice(id: number, body: Record<string, unknown>): Promise<EvohomeDevice> {
  const res = await api.patch(`/evohome/devices/${id}`, body);
  return res.data;
}

export async function deleteEvohomeDevice(id: number): Promise<void> {
  await api.delete(`/evohome/devices/${id}`);
}

export async function reorderEvohomeDevices(items: { id: number; orden: number }[]): Promise<void> {
  await api.put('/evohome/devices/reorder', { items });
}

/** Reordenar zonas solo en Clima → Horarios (no toca el orden de tarjetas de habitación). */
export async function reorderEvohomeHorario(
  items: { id: number; orden_horario: number }[],
): Promise<void> {
  await api.put('/evohome/devices/reorder-horario', { items });
}

export async function evohomeControl(device_id: number, action: string, value?: unknown): Promise<any> {
  const res = await api.post('/evohome/control', { device_id, action, value });
  return res.data;
}

export async function evohomeRefreshDevice(
  device_id: number,
  opts?: { force?: boolean },
): Promise<EvohomeDevice> {
  const res = await api.post(`/evohome/devices/${device_id}/refresh`, null, {
    params: opts?.force ? { force: true } : undefined,
  });
  return res.data;
}

export async function fetchEvohomeTempSeries(params: {
  deviceId: number;
  startDate: string;
  endDate: string;
  interval: string;
}): Promise<{ ok: boolean; data: EvohomeTempPoint[] }> {
  const res = await api.get('/evohome/historical/temperature-time-series', {
    params: {
      device_id: params.deviceId,
      start_date: params.startDate,
      end_date: params.endDate,
      interval: params.interval,
    },
  });
  return res.data;
}

export async function listEvohomePolicies(): Promise<EvohomePolicyRow[]> {
  try {
    const res = await api.get<{ policies: EvohomePolicyRow[] }>('/evohome/ai-policies');
    return res.data.policies || [];
  } catch {
    return [];
  }
}

export async function getEvohomePolicy(deviceId: number): Promise<EvohomePolicyRow | null> {
  try {
    const res = await api.get<EvohomePolicyRow>(`/evohome/devices/${deviceId}/ai-policy`);
    return res.data;
  } catch {
    return null;
  }
}

export async function upsertEvohomePolicy(
  deviceId: number,
  body: { whitelist?: boolean; blacklist?: boolean; require_confirmation?: boolean },
): Promise<EvohomePolicyRow> {
  const res = await api.put(`/evohome/devices/${deviceId}/ai-policy`, body);
  return res.data;
}

export async function deleteEvohomePolicy(deviceId: number): Promise<void> {
  await api.delete(`/evohome/devices/${deviceId}/ai-policy`);
}

/** Periodo de un día en el schedule EvoHome. */
export interface EvohomeScheduleSwitchpoint {
  time_of_day: string;
  heat_setpoint: number;
}

export interface EvohomeScheduleDay {
  day_of_week: string;
  switchpoints: EvohomeScheduleSwitchpoint[];
}

export interface EvohomeScheduleCapabilities {
  min_switchpoints_per_day?: number;
  max_switchpoints_per_day?: number;
  timing_resolution?: string;
  setpoint_value_resolution?: number;
  min_heat_setpoint?: number;
  max_heat_setpoint?: number;
}

export interface EvohomeScheduleResponse {
  ok?: boolean;
  device_id: number;
  nombre?: string;
  location_id?: number | null;
  daily_schedules: EvohomeScheduleDay[];
  capabilities?: EvohomeScheduleCapabilities;
  this_switchpoint?: { at?: string; heat_setpoint?: number | null } | null;
  next_switchpoint?: { at?: string; heat_setpoint?: number | null } | null;
}

export async function fetchEvohomeSchedule(deviceId: number): Promise<EvohomeScheduleResponse> {
  const res = await api.get(`/evohome/devices/${deviceId}/schedule`);
  return res.data;
}

export interface EvohomeLocationScheduleEntry {
  device_id: number;
  nombre?: string;
  external_id?: string;
  daily_schedules: EvohomeScheduleDay[] | null;
  capabilities?: EvohomeScheduleCapabilities | null;
  fetched_at?: string | null;
  age_s?: number | null;
  stale?: boolean;
  cached?: boolean;
}

export interface EvohomeLocationSchedulesResponse {
  ok?: boolean;
  location_id: number;
  schedules: EvohomeLocationScheduleEntry[];
  refreshed_count?: number;
  from_cache_only?: boolean;
  errors?: { device_id?: number; error?: string }[];
}

/** Schedules de todas las zonas de una locación (cache DB y/o refresh TCC). */
export async function fetchEvohomeLocationSchedules(
  locationId: number,
  opts?: { cacheOnly?: boolean; force?: boolean },
): Promise<EvohomeLocationSchedulesResponse> {
  const params: Record<string, boolean> = {};
  if (opts?.cacheOnly) params.cache_only = true;
  if (opts?.force) params.force = true;
  const res = await api.get(`/evohome/locations/${locationId}/schedules`, { params });
  return res.data;
}

export async function putEvohomeSchedule(
  deviceId: number,
  daily_schedules: EvohomeScheduleDay[],
): Promise<EvohomeScheduleResponse> {
  const res = await api.put(`/evohome/devices/${deviceId}/schedule`, { daily_schedules });
  return res.data;
}

export async function copyEvohomeScheduleToZone(
  sourceDeviceId: number,
  targetDeviceId: number,
): Promise<any> {
  const res = await api.post(`/evohome/devices/${sourceDeviceId}/schedule/copy-to`, {
    target_device_id: targetDeviceId,
  });
  return res.data;
}

export async function copyEvohomeScheduleToZones(
  sourceDeviceId: number,
  targetDeviceIds: number[],
  dailySchedules?: EvohomeScheduleDay[],
): Promise<any> {
  const body: Record<string, unknown> = {
    target_device_ids: targetDeviceIds,
  };
  if (dailySchedules) body.daily_schedules = dailySchedules;
  const res = await api.post(`/evohome/devices/${sourceDeviceId}/schedule/copy-to`, body);
  return res.data;
}
